import type { WavesChart, WavesObstacle, WavesTerrain } from '@shared/wavesChart';
import { resolveWavesTerrain } from '@shared/wavesChart';
import type { WavesLayout } from '../wavesPlayfieldGeometry';
import { OBSTACLE_BEATS_VISIBLE_AHEAD } from '../wavesPlayfieldGeometry';
import {
  OBSTACLE_APPROACH_PULSE_BEATS,
  OBSTACLE_DRAW_BEATS_AHEAD,
} from './wavesRenderConstants';
import { drawWavesObstacle } from './wavesDrawObstacles';

/**
 * Obstacle render pass with a monotonic `firstVisibleIdx` cursor so
 * per-frame work is O(visible) instead of O(chart.obstacles). On
 * dense Extreme charts that can mean hundreds of skipped iterations
 * per frame at 60 fps.
 *
 * Passed obstacles continue to scroll past the player anchor at the
 * normal scroll rate and disappear off the left edge — no freezing
 * or stacking. The score pop + flash that fire on `obstacle_passed`
 * handle the verdict feedback.
 */
export class WavesObstaclesPass {
  private firstVisibleIdx = 0;
  private lastChart: WavesChart | null = null;
  private sortedObstacles: ReadonlyArray<WavesObstacle> = [];
  private terrain: WavesTerrain | null = null;

  reset(): void {
    this.firstVisibleIdx = 0;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    layout: WavesLayout,
    chart: WavesChart,
    beat: number,
    chromeShadows: boolean,
  ): void {
    if (this.lastChart !== chart) {
      this.lastChart = chart;
      this.sortedObstacles = [...chart.obstacles].sort((a, b) => a.beat - b.beat);
      this.terrain = resolveWavesTerrain(chart);
      this.firstVisibleIdx = 0;
    }
    const terrain = this.terrain!;

    const palette = chart.palette;
    const obstacles = this.sortedObstacles;
    while (this.firstVisibleIdx < obstacles.length) {
      const obs = obstacles[this.firstVisibleIdx];
      if (obs.beat < beat - OBSTACLE_BEATS_VISIBLE_AHEAD) {
        this.firstVisibleIdx += 1;
      } else {
        break;
      }
    }

    for (let i = this.firstVisibleIdx; i < obstacles.length; i += 1) {
      const obs = obstacles[i];
      const beatsAhead = obs.beat - beat;
      if (beatsAhead > OBSTACLE_DRAW_BEATS_AHEAD) {
        break;
      }
      const fadeIn = Math.min(1, (OBSTACLE_DRAW_BEATS_AHEAD - beatsAhead) / 1.5);
      ctx.globalAlpha = 0.85 * fadeIn;
      const approachPulse =
        beatsAhead < 0
          ? 0
          : Math.max(0, 1 - beatsAhead / OBSTACLE_APPROACH_PULSE_BEATS);
      drawWavesObstacle(
        ctx,
        obs,
        layout,
        terrain,
        beat,
        chart.playerRadius,
        palette.hazard,
        approachPulse,
        chromeShadows,
      );
      ctx.globalAlpha = 1;
    }
  }
}
